import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, ArrowLeft, Loader2, Receipt, Calendar, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!sessionId || !token) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPaymentDetails(res.data);
      } catch (err) {
        console.error('Failed to fetch payment status:', err);
        setError('Could not fetch payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [sessionId, token]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-8" data-testid="payment-success-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-black text-green-600">PAYMENT SUCCESSFUL</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            Thank you! Your payment has been processed successfully.
          </p>
          
          {paymentDetails && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Amount
                </span>
                <span className="font-bold text-lg">
                  ${paymentDetails.amount?.toFixed(2) || '0.00'} {paymentDetails.currency?.toUpperCase()}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Status
                </span>
                <Badge variant={paymentDetails.payment_status === 'paid' ? 'default' : 'secondary'}>
                  {paymentDetails.payment_status === 'paid' ? 'Paid' : paymentDetails.payment_status}
                </Badge>
              </div>
              
              {sessionId && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Reference
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">
                    {sessionId.slice(0, 20)}...
                  </span>
                </div>
              )}
            </div>
          )}
          
          {error && (
            <p className="text-center text-sm text-yellow-600 bg-yellow-500/10 p-2 rounded">
              {error}
            </p>
          )}
          
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => navigate('/dashboard')} className="w-full gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go to Dashboard
            </Button>
            <Button onClick={() => navigate('/tournaments')} variant="outline" className="w-full">
              Browse Tournaments
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-8" data-testid="payment-cancel-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <CardTitle className="text-2xl font-black text-red-600">PAYMENT CANCELLED</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            Your payment was cancelled. No charges were made to your account.
          </p>
          
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              If you experienced any issues or have questions about our payment process, 
              please contact support.
            </p>
          </div>
          
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => navigate(-1)} variant="outline" className="w-full gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/tournaments')} className="w-full">
              Browse Tournaments
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
